import React from 'react';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import { ButtonRow } from '@components/styles';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputNumber } from '@components/framework/input/InputNumber';
import { MultiSelect } from 'primereact/multiselect';
import { Dropdown } from '@components/RingilComponents/SafeDropdown';
import { Field } from '@components/framework/formfield/Field.tsx';
import SectionPanel from '@components/framework/panels/SectionPanel.tsx';
import RingilInputSwitch from '@components/framework/input/RingilInputSwitch/RingilInputSwitch.tsx';
import CurrencyDropdown from '@components/ringil3/Input/InputPriceWithCurrency/CurrencyDropdown.tsx';
import CurrencyMultiselect from '@components/ringil3/Input/InputPriceWithCurrency/CurrencyMultiselect.tsx';
import { LovDTO, PriceDisplayTypeEnum, ResponseDeadlineModeEnum } from '@api/ringil-api.v2';
import { Form as FormApi, proxiedPropertiesOf } from '@hooks/useForm/useForm';
import { Form } from '@components/pages/admin/companies/adminCompanyDetailForm.ts';
import { NullableInput } from '@components/pages/admin/companies/AdminCompanyDetailInputs.tsx';
import SvgCog from '@icons/cog.svg?react';

interface ComponentProps {
  form: FormApi<Form>;
  allReqs: { label: string; value: string }[];
  cargoTemplates: LovDTO[];
  externalIdTypes: { label: string; value: string }[];
  onSave: () => void;
  saving: boolean;
}

const names = proxiedPropertiesOf<Form>();

const GeneralSection: React.FC<ComponentProps> = ({ form, allReqs, cargoTemplates, externalIdTypes, onSave, saving }) => {
  const { tr } = useTranslationLgs();

  return (
    <SectionPanel
      icon={<SvgCog className={'w-6 h-6'} />}
      iconColor={'var(--content-tertiary)'}
      heading={tr('adminCompanyDetail.sectionGeneral', 'General')}
      dataTestId={'admin-company-section-general'}>
      <div className={'grid grid-cols-1 lg:grid-cols-3 gap-4 items-start'}>
        <Field label={tr('adminCompanyDetail.contentPrefix', 'Context prefix')}>
          <NullableInput value={form.values.contentPrefix} onChange={v => form.setFieldValue(names.contentPrefix, v)} defaultValue={''}>
            <InputText value={form.values.contentPrefix} onChange={e => form.setFieldValue(names.contentPrefix, e.target.value)} />
          </NullableInput>
        </Field>
        <Field label={'customerReportingId'}>
          <NullableInput value={form.values.customerReportingId} onChange={v => form.setFieldValue(names.customerReportingId, v)} defaultValue={[]}>
            <InputText value={form.values.customerReportingId} onChange={v => form.setFieldValue(names.customerReportingId, v.target.value)} />
          </NullableInput>
        </Field>
        <Field label={'dachserLastExportId'}>
          <NullableInput value={form.values.dachserLastExportId} onChange={v => form.setFieldValue(names.dachserLastExportId, v)} defaultValue={'00000'}>
            <InputText value={form.values.dachserLastExportId} onChange={v => form.setFieldValue(names.dachserLastExportId, v.target.value)} />
          </NullableInput>
        </Field>

        <div className={'lg:col-span-3'}>
          <Field label={'enabledExpRequirementCodes'}>
            <NullableInput value={form.values.enabledExpRequirementCodes} onChange={v => form.setFieldValue(names.enabledExpRequirementCodes, v)} defaultValue={[]}>
              <MultiSelect
                options={allReqs}
                optionLabel="label"
                optionValue="value"
                value={form.values.enabledExpRequirementCodes}
                onChange={v => form.setFieldValue(names.enabledExpRequirementCodes, v.value)}
                filter
              />
            </NullableInput>
          </Field>
        </div>
        <div className={'lg:col-span-3'}>
          <Field label={'prefillRequirementCodes'}>
            <NullableInput value={form.values.prefillRequirementCodes} onChange={v => form.setFieldValue(names.prefillRequirementCodes, v)} defaultValue={[]}>
              <MultiSelect
                options={allReqs}
                optionLabel="label"
                optionValue="value"
                value={form.values.prefillRequirementCodes}
                onChange={v => form.setFieldValue(names.prefillRequirementCodes, v.value)}
                filter
              />
            </NullableInput>
          </Field>
        </div>
        <div className={'lg:col-span-3'}>
          <Field label={tr('adminCompanyDetail.cargoTemplates', 'Cargo templates')}>
            <NullableInput value={form.values.cargoTemplateIds} onChange={v => form.setFieldValue(names.cargoTemplateIds, v)} defaultValue={[]}>
              <MultiSelect
                options={cargoTemplates}
                optionLabel="name"
                optionValue="id"
                value={form.values.cargoTemplateIds}
                onChange={v => form.setFieldValue(names.cargoTemplateIds, v.value)}
                filter
              />
            </NullableInput>
          </Field>
        </div>

        <Field label={tr('adminCompanyDetail.availableCurrencies', 'Available currencies override (empty list means use default)')}>
          <CurrencyMultiselect selectedCurrencies={form.values.availableCurrencies} onSelected={v => form.setFieldValue(names.availableCurrencies, v)} />
        </Field>
        <Field label={tr('adminCompanyDetail.defaultCurrency', 'Default currency')}>
          <div>
            <CurrencyDropdown selectedCurrency={form.values.defaultCurrencyIsoCode} onSelected={v => form.setFieldValue(names.defaultCurrencyIsoCode, v)} fullWidth />
          </div>
        </Field>
        <Field label={tr('adminCompanyDetail.priceDisplayType', 'Price display type')}>
          <Dropdown
            value={form.values.priceDisplayType}
            options={Object.values(PriceDisplayTypeEnum).map(type => ({
              label: tr(`AdminCompanyDetail.priceDisplayType${type}`, type),
              value: type,
            }))}
            onChange={e => form.setFieldValue(names.priceDisplayType, e.value)}
          />
        </Field>

        <Field label={'distributionDefaultHoursInFuture'}>
          <InputNumber value={form.values.defaultDistributionIntervalInHours} onChange={v => form.setFieldValue(names.defaultDistributionIntervalInHours, v.value)} />
        </Field>
        <Field label={tr('AdminCompanyDetail.carrierQuoteValidTillDefaultInHours', 'Carrier quote valid till default (hours)')}>
          <InputNumber value={form.values.carrierQuoteValidTillDefaultInHours} onChange={v => form.setFieldValue(names.carrierQuoteValidTillDefaultInHours, v.value)} />
        </Field>
        <Field label={tr('AdminCompanyDetail.responseDeadlineMode', 'Carrier response deadline default')}>
          <Dropdown
            value={form.values.responseDeadlineMode}
            options={Object.values(ResponseDeadlineModeEnum).map(mode => ({
              label: tr(`AdminCompanyDetail.responseDeadlineMode${mode}`, mode),
              value: mode,
            }))}
            onChange={e => form.setFieldValue(names.responseDeadlineMode, e.value)}
          />
        </Field>

        <Field label={tr('adminCompanyDetail.externalIdTypes', 'External IDs types')}>
          <NullableInput value={form.values.externalIdTypes} onChange={v => form.setFieldValue(names.externalIdTypes, v)} defaultValue={[]}>
            <MultiSelect
              options={externalIdTypes}
              optionLabel="label"
              optionValue="value"
              value={form.values.externalIdTypes}
              onChange={v => form.setFieldValue(names.externalIdTypes, v.value)}
              filter
            />
          </NullableInput>
        </Field>
        <RingilInputSwitch
          value={form.values.autoAcceptModuleForInherited}
          onChange={v => form.setFieldValue(form.names.autoAcceptModuleForInherited, v)}
          label={tr('AdminCompanyDetail.auto_accept_modules', 'Auto accept modules')}
          className={'lg:col-start-1'}
        />
      </div>
      <ButtonRow>
        <Button label={tr('generic.save', 'Save')} onClick={onSave} loading={saving} disabled={saving} />
      </ButtonRow>
    </SectionPanel>
  );
};

export default GeneralSection;
